import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { SrIcon } from '@/components/ui/SrIcon';

export interface DrillBack {
  label: string;
  onClick: () => void;
}

interface DrillPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Small uppercase kicker above the title. Replaced by the back link when `back` is set. */
  kicker?: string;
  /** Nested level: renders «‹ label» that returns to the previous level. */
  back?: DrillBack;
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Felles drill-panel for Måltall-widgetene (VIB-2990). Sidepanel fra høyre,
 * fullskjerm på mobil. Bygget på Radix Dialog (fokus-trap, body-scroll-lock,
 * Escape og klikk-utenfor). Nestede nivåer bruker `back` i stedet for kicker.
 */
export function DrillPanel({ isOpen, onClose, kicker, back, title, children, className }: DrillPanelProps) {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgba(8,31,59,0.28)]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed inset-y-0 right-0 z-50 w-full sm:w-[520px] max-w-full',
            'bg-white border-l-[1.5px] border-foreground flex flex-col',
            'shadow-[-24px_0_48px_-24px_rgba(2,10,31,0.4)]',
            'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:slide-in-from-right motion-safe:data-[state=open]:duration-200',
            'focus:outline-none',
            className,
          )}
        >
          <div className="px-4 sm:px-6 pt-[calc(22px+env(safe-area-inset-top,0px))] pb-4 border-b border-foreground/[0.08] flex items-start justify-between gap-4 shrink-0">
            <div className="min-w-0">
              {back ? (
                <button
                  type="button"
                  onClick={back.onClick}
                  className="text-[13px] font-semibold text-primary mb-1.5 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                >
                  ‹ {back.label}
                </button>
              ) : (
                kicker && (
                  <p className="text-[11px] uppercase tracking-[0.07em] text-[#8A97A3] font-semibold mb-1.5">
                    {kicker}
                  </p>
                )
              )}
              <DialogPrimitive.Title asChild>
                <h2 className="font-display font-semibold text-[24px] text-foreground tracking-[-0.01em] leading-tight">
                  {title}
                </h2>
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label="Lukk"
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-foreground/[0.06] transition-all hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <SrIcon name="x" className="w-4 h-4 text-foreground" />
              </button>
            </DialogPrimitive.Close>
          </div>

          <div className="overflow-y-auto overflow-x-hidden flex-1 pb-[env(safe-area-inset-bottom,0px)]">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
